import {
  ActionResultType,
  DecisionActionOutcome,
  DecisionConfidence,
  DecisionConfidenceCompact,
  DecisionPriority,
  DecisionPriorityCompact,
  DecisionRisk,
  DecisionRiskCompact,
  DecisionState,
  ReevaluationStatus,
} from "../../../models/home";
import {
  HomeConfidenceCompactResponse,
  HomeConfidenceResponse,
  HomeIaActionHttpMethodResponse,
  HomeIaActionResultTypeResponse,
  HomeIaDecisionActionOutcomeResponse,
  HomeIaReevaluationStatusResponse,
  HomePriorityBreakdownCompactResponse,
  HomePriorityBreakdownResponse,
  HomeRiskExplanationCompactResponse,
  HomeRiskExplanationResponse
} from "../../../models/home/dtos";


export function toPriorityCompactViewModel(
    dto?: HomePriorityBreakdownCompactResponse | null
  ): DecisionPriorityCompact {
    return {
      score: dto?.score ?? 0,
      tier: dto?.tier ?? undefined,
      finalReason: dto?.finalReason ?? undefined
    };
  }

export function toPriorityViewModel(dto?: HomePriorityBreakdownResponse | null): DecisionPriority {
    return {
      score: dto?.score ?? 0,
      tier: dto?.tier ?? undefined,
      finalReason: dto?.finalReason ?? undefined,
      base: dto?.base ?? undefined,
      urgency: dto?.urgency ?? undefined,
      dateProximity: dto?.dateProximity ?? undefined,
      riskBoost: dto?.riskBoost ?? undefined,
      artifactBoost: dto?.artifactBoost ?? undefined,
      userStateAdjustment: dto?.userStateAdjustment ?? undefined
    };
  }

export function toRiskCompactViewModel(dto?: HomeRiskExplanationCompactResponse | null): DecisionRiskCompact {
    return {
      level: dto?.level ?? undefined,
      category: dto?.category ?? undefined,
      reason: dto?.reason ?? undefined
    };
  }

export function toRiskViewModel(dto?: HomeRiskExplanationResponse | null): DecisionRisk {
    return {
      level: dto?.level ?? undefined,
      category: dto?.category ?? undefined,
      reason: dto?.reason ?? undefined,
      impactAreas: dto?.impactAreas ?? [],
      escalationTriggers: dto?.escalationTriggers ?? [],
      evidenceIds: dto?.evidenceIds ?? []
    };
  }

export function toConfidenceCompactViewModel(
    dto?: HomeConfidenceCompactResponse | null
  ): DecisionConfidenceCompact {
    return {
      overall: dto?.overall ?? 0,
      level: dto?.level ?? undefined,
      warnings: dto?.warnings ?? []
    };
  }

export function toConfidenceViewModel(dto?: HomeConfidenceResponse | null): DecisionConfidence {
    return {
      overall: dto?.overall ?? 0,
      level: dto?.level ?? undefined,
      classification: dto?.classification ?? undefined,
      dateExtraction: dto?.dateExtraction ?? undefined,
      actionSelection: dto?.actionSelection ?? undefined,
      explanation: dto?.explanation ?? undefined,
      warnings: dto?.warnings ?? []
    };
  }

export function toOptionalState(value?: string | null): DecisionState | undefined {
    if (!value) {
      return undefined;
    }
    return toState(value);
  }

export function toOptionalExpectedState(value?: string | null): DecisionState | "reevaluate" | undefined {
    if (!value) {
      return undefined;
    }

    if (value === "reevaluate") {
      return value;
    }

    return isKnownState(value) ? toState(value) : undefined;
  }

export function toOptionalActionHttpMethod(
    value?: HomeIaActionHttpMethodResponse | null
  ): "POST" | "PUT" | "PATCH" | "DELETE" | undefined {
    switch (value) {
      case "POST":
      case "PUT":
      case "PATCH":
      case "DELETE":
        return value;
      default:
        return undefined;
    }
  }

export function toOptionalActionResultType(
    value?: HomeIaActionResultTypeResponse | null
  ): ActionResultType | undefined {
    switch (value) {
      case "state_changed":
      case "reevaluation_queued":
      case "inspection_only":
      case "rejected":
      case "replay":
        return value;
      default:
        return undefined;
    }
  }

export function toOptionalReevaluationStatus(
    value?: HomeIaReevaluationStatusResponse | null
  ): ReevaluationStatus | undefined {
    switch (value) {
      case "pending":
      case "completed":
      case "failed":
      case "not_required":
        return value;
      default:
        return undefined;
    }
  }

export function toOptionalActionOutcome(
    value?: HomeIaDecisionActionOutcomeResponse | null
  ): DecisionActionOutcome | undefined {
    switch (value) {
      case "business_action_acknowledged":
      case "manual_business_completion":
        return value;
      default:
        return undefined;
    }
  }

export function isKnownState(value: string): boolean {
    switch (normalizeState(value)) {
      case "act_now":
      case "act_soon":
      case "act_without_rush":
      case "waiting":
      case "blocked":
      case "in_review":
      case "dismissed":
      case "safe_to_ignore":
      case "completed":
        return true;
      default:
        return false;
    }
  }

export function toState(value?: string | null): DecisionState {
    const normalized = normalizeState(value);
    switch (normalized) {
      case "act_now":
        return "act_now";
      case "act_soon":
        return "act_soon";
      case "act_without_rush":
      case "safe_to_ignore":
        return "act_without_rush";
      case "blocked":
        return "blocked";
      case "in_review":
        return "in_review";
      case "dismissed":
        return "dismissed";
      case "completed":
        return "completed";
      case "waiting":
      default:
        return "waiting";
    }
  }

export function normalizeState(value?: string | null): string {
    return (value ?? "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");
  }
